import { c as _c } from "react/compiler-runtime";
import { useTwilioMessageServices } from 'modules/profile/integrations/twilio/hooks/use-twilio-message-services';
import { useTwilioPhoneNumbers } from 'modules/profile/integrations/twilio/hooks/use-twilio-phone-numbers';
import { SenderTypeEnum, SmsTemplateCreateInterface } from 'modules/sms/types';
import { SmsTemplateErrorsInterface } from 'modules/sms/types/errors-interface';
import { useEffect, useMemo } from 'react';
import { Button, FormInput, FormSelect, Hint, RadioGroup } from 'shared/components';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { SectionMessage } from 'shared/components/section-message';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
export type SmsSenderFields = Pick<SmsTemplateCreateInterface, 'senderType' | 'phoneNumber' | 'alphanumericSender' | 'messageServiceSid'>;
export type SmsSenderErrors = Pick<SmsTemplateErrorsInterface, 'phoneNumber' | 'alphanumericSender' | 'messageServiceSid'>;
interface SmsTemplateRadioGroupSenderTypeProps {
  values: SmsSenderFields;
  onChange: (values: SmsSenderFields) => void;
  errors: SmsSenderErrors;
  isFetching: boolean;
  isPreFetching: boolean;
  disabled?: boolean;
  /** Hides the "Sender" group label where the surrounding tab already names the section. */
  hideLabel?: boolean;
  /** Shows a missing Twilio account as an alert (SMS template editors) instead of inline text. */
  withTwilioAlert?: boolean;
  onTwilioHasErrors?: (hasTwilioErrors: boolean) => void;
}
export const SmsTemplateRadioGroupSenderType = t0 => {
  const $ = _c(100);
  const {
    values,
    onChange,
    errors,
    isFetching,
    isPreFetching,
    disabled,
    hideLabel,
    withTwilioAlert,
    onTwilioHasErrors
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    data: twilioPhoneNumbers,
    errorNotFound: twilioPhoneNumbersNotFoundError
  } = useTwilioPhoneNumbers();
  const {
    data: twilioMessageServices,
    errorNotFound: twilioMessageServicesNotFoundError
  } = useTwilioMessageServices();
  let t1;
  if ($[0] !== twilioPhoneNumbers || $[1] !== twilioPhoneNumbersNotFoundError) {
    t1 = !!twilioPhoneNumbersNotFoundError ? [] : twilioPhoneNumbers?.map(_temp);
    $[0] = twilioPhoneNumbers;
    $[1] = twilioPhoneNumbersNotFoundError;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const phoneNumbers = t1;
  let t2;
  if ($[3] !== twilioMessageServices || $[4] !== twilioMessageServicesNotFoundError) {
    t2 = !!twilioMessageServicesNotFoundError ? [] : twilioMessageServices?.map(_temp2);
    $[3] = twilioMessageServices;
    $[4] = twilioMessageServicesNotFoundError;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const messageServices = t2;
  const hasTwilioErrors = !!(twilioPhoneNumbersNotFoundError || twilioMessageServicesNotFoundError);
  let t3;
  if ($[6] !== onChange || $[7] !== values) {
    t3 = updates => {
      onChange({
        ...values,
        ...updates
      });
    };
    $[6] = onChange;
    $[7] = values;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleChange = t3;
  let t4;
  if ($[9] !== hasTwilioErrors || $[10] !== onTwilioHasErrors) {
    t4 = () => {
      onTwilioHasErrors?.(hasTwilioErrors);
    };
    $[9] = hasTwilioErrors;
    $[10] = onTwilioHasErrors;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== hasTwilioErrors) {
    t5 = [hasTwilioErrors];
    $[12] = hasTwilioErrors;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== hideLabel || $[15] !== t) {
    t6 = hideLabel ? {
      ariaLabel: t("dashboard.sms.sender")
    } : {
      label: t("dashboard.sms.sender")
    };
    $[14] = hideLabel;
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const label = t6;
  const t7 = disabled || isFetching;
  const t8 = values.senderType;
  let t9;
  if ($[17] !== handleChange) {
    t9 = senderType => handleChange({
      senderType
    });
    $[17] = handleChange;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t("dashboard.sms.sender_phone_number");
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t) {
    t11 = t("dashboard.sms.select_number");
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = disabled || isFetching;
  let t13;
  if ($[23] !== handleChange) {
    t13 = val => handleChange({
      phoneNumber: val || null
    });
    $[23] = handleChange;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = !values.phoneNumber;
  let t15;
  if ($[25] !== errors.phoneNumber || $[26] !== isPreFetching || $[27] !== phoneNumbers || $[28] !== t11 || $[29] !== t12 || $[30] !== t13 || $[31] !== t14 || $[32] !== values.phoneNumber) {
    t15 = <FormSelect placeholder={t11} value={values.phoneNumber} data={phoneNumbers} isPreFetching={isPreFetching} disabled={t12} onChange={t13} error={errors.phoneNumber} withoutCloseIcon={t14} />;
    $[25] = errors.phoneNumber;
    $[26] = isPreFetching;
    $[27] = phoneNumbers;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = values.phoneNumber;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t10 || $[35] !== t15) {
    t16 = {
      id: SenderTypeEnum.PhoneNumber,
      caption: t10,
      content: t15
    };
    $[34] = t10;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t) {
    t17 = t("dashboard.sms.alphanumeric_sender");
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t) {
    t18 = t("dashboard.sms.alphanumeric_sender");
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t18) {
    t19 = <SenderTypeHint ariaLabel={t18} i18nKey="dashboard.sms.alphanumeric_sender_tooltip" href="https://www.twilio.com/docs/glossary/what-alphanumeric-sender-id" />;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t) {
    t20 = t("dashboard.sms.alphanumeric_sender_placeholder");
    $[43] = t;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const t21 = values.alphanumericSender || "";
  let t22;
  if ($[45] !== handleChange) {
    t22 = event => handleChange({
      alphanumericSender: event.target.value || null
    });
    $[45] = handleChange;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  const t23 = disabled || isFetching;
  let t24;
  if ($[47] !== errors.alphanumericSender || $[48] !== isPreFetching || $[49] !== t20 || $[50] !== t21 || $[51] !== t22 || $[52] !== t23) {
    t24 = <FormInput placeholder={t20} value={t21} onChange={t22} isPreFetching={isPreFetching} disabled={t23} error={errors.alphanumericSender} />;
    $[47] = errors.alphanumericSender;
    $[48] = isPreFetching;
    $[49] = t20;
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t17 || $[55] !== t19 || $[56] !== t24) {
    t25 = {
      id: SenderTypeEnum.Alphanumeric,
      caption: t17,
      additionalInfo: t19,
      content: t24
    };
    $[54] = t17;
    $[55] = t19;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t) {
    t26 = t("dashboard.sms.messaging_services");
    $[58] = t;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t) {
    t27 = t("dashboard.sms.messaging_services");
    $[60] = t;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t27) {
    t28 = <SenderTypeHint ariaLabel={t27} i18nKey="dashboard.sms.messaging_services_tooltip" href="https://www.twilio.com/docs/messaging/services" />;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  const t29 = disabled || isFetching;
  let t30;
  if ($[64] !== handleChange) {
    t30 = val_0 => handleChange({
      messageServiceSid: val_0 || null
    });
    $[64] = handleChange;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  const t31 = !values.messageServiceSid;
  let t32;
  if ($[66] !== errors.messageServiceSid || $[67] !== isPreFetching || $[68] !== messageServices || $[69] !== t29 || $[70] !== t30 || $[71] !== t31 || $[72] !== values.messageServiceSid) {
    t32 = <FormSelect value={values.messageServiceSid} data={messageServices} isPreFetching={isPreFetching} disabled={t29} onChange={t30} error={errors.messageServiceSid} withoutCloseIcon={t31} />;
    $[66] = errors.messageServiceSid;
    $[67] = isPreFetching;
    $[68] = messageServices;
    $[69] = t29;
    $[70] = t30;
    $[71] = t31;
    $[72] = values.messageServiceSid;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  let t33;
  if ($[74] !== t26 || $[75] !== t28 || $[76] !== t32) {
    t33 = {
      id: SenderTypeEnum.MessageService,
      caption: t26,
      additionalInfo: t28,
      content: t32
    };
    $[74] = t26;
    $[75] = t28;
    $[76] = t32;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== t16 || $[79] !== t25 || $[80] !== t33) {
    t34 = [t16, t25, t33];
    $[78] = t16;
    $[79] = t25;
    $[80] = t33;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== label || $[83] !== t34 || $[84] !== t7 || $[85] !== t9 || $[86] !== values.senderType) {
    t35 = <RadioGroup {...label} required={true} orientation="vertical" disabled={t7} selected={t8} onChange={t9} options={t34} />;
    $[82] = label;
    $[83] = t34;
    $[84] = t7;
    $[85] = t9;
    $[86] = values.senderType;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== hasTwilioErrors || $[89] !== t || $[90] !== withTwilioAlert) {
    t36 = hasTwilioErrors && withTwilioAlert && <div data-test="sms-twilio-not-found"><SectionMessage variant="danger" title={t("dashboard.sms.twilio_required.title")}><Button variant="link" render={<LinkWithoutPrefetch target="_blank" href="/profile/integrations" />}>{t("dashboard.sms.twilio_required.connect")}</Button></SectionMessage></div>;
    $[88] = hasTwilioErrors;
    $[89] = t;
    $[90] = withTwilioAlert;
    $[91] = t36;
  } else {
    t36 = $[91];
  }
  let t37;
  if ($[92] !== hasTwilioErrors || $[93] !== t || $[94] !== withTwilioAlert) {
    t37 = hasTwilioErrors && !withTwilioAlert && <p className="text-text-error" data-test="sms-twilio-not-found"><TransLoco t={t} i18nKey="dashboard.sms.twilio_not_found" components={{
        a: <LinkWithoutPrefetch className="link" target="_blank" href="/profile/integrations" />
      }} /></p>;
    $[92] = hasTwilioErrors;
    $[93] = t;
    $[94] = withTwilioAlert;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  let t38;
  if ($[96] !== t35 || $[97] !== t36 || $[98] !== t37) {
    t38 = <div className="flex flex-col gap-3">{t35}{t36}{t37}</div>;
    $[96] = t35;
    $[97] = t36;
    $[98] = t37;
    $[99] = t38;
  } else {
    t38 = $[99];
  }
  return t38;
};
interface SenderTypeHintProps {
  ariaLabel: string;
  href: string;
  i18nKey: string;
}
const SenderTypeHint = t0 => {
  const $ = _c(9);
  const {
    ariaLabel,
    href,
    i18nKey
  } = t0;
  const {
    t
  } = useLocoTranslation();
  let t1;
  if ($[0] !== href) {
    t1 = {
      a: <a target="_blank" rel="noreferrer" className="link" href={href} />
    };
    $[0] = href;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== i18nKey || $[3] !== t || $[4] !== t1) {
    t2 = <TransLoco t={t} i18nKey={i18nKey} components={t1} />;
    $[2] = i18nKey;
    $[3] = t;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== ariaLabel || $[7] !== t2) {
    t3 = <Hint ariaLabel={ariaLabel} label={t2} />;
    $[6] = ariaLabel;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
function _temp(phoneNumber) {
  return {
    id: phoneNumber,
    caption: phoneNumber
  };
}
function _temp2(messageService) {
  return {
    id: messageService.sid,
    caption: messageService.name
  };
}